import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isGuest } from '../lib/guestMode';
import GuestEmptyState from '../components/GuestEmptyState';
import { useProfile } from '../context/ProfileContext';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import TutorialOverlay, { TutorialBackdrop } from '../components/TutorialOverlay';
import CardImage from '../components/CardImage';
import ChaseWaveImage from '../assets/images/ChaseWave.png';

// Icons
const CalendarIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ArrowDownCircleIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12l4 4 4-4" />
    <path d="M12 8v8" />
  </svg>
);

const ArrowUpCircleIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M16 12l-4-4-4 4" />
    <path d="M12 16V8" />
  </svg>
);

const CheckCircleIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const XCircleIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6" />
    <path d="M9 9l6 6" />
  </svg>
);

const ClockIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const StarIcon = ({ filled = false, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "#ffd700" : "none"} stroke="#ffd700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ChevronRightIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// Helper to get current user ID from users table
const getCurrentUserId = async () => {
  const { data } = await supabase.auth.getSession();
  const authId = data?.session?.user?.id;
  if (!authId) return null;

  const { data: userData, error } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', authId)
    .single();

  if (error || !userData) {
    console.error('Error fetching user ID from users table:', error);
    return null;
  }

  return userData.id;
};

// Fetch upcoming meetups from trade_schedules (confirmed only, grouped by partner)
const fetchMeetups = async (userId) => {
  // First get trade requests where the user is involved
  const { data: userTrades, error: tradesError } = await supabase
    .from('trade_requests')
    .select(`
      id,
      requester_id,
      card_owner_id,
      cards (name, digital_image_url),
      requester:users!trade_requests_requester_id_fkey (id, username),
      card_owner:users!trade_requests_card_owner_id_fkey (id, username)
    `)
    .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
    .eq('status', 'accepted');

  if (tradesError) {
    console.error('Error fetching user trades:', tradesError);
    return [];
  }

  if (!userTrades || userTrades.length === 0) {
    return [];
  }

  const tradeIds = userTrades.map(trade => trade.id);

  // Now get the CONFIRMED scheduled meetups for those trades
  const { data: schedules, error } = await supabase
    .from('trade_schedules')
    .select(`
      trade_id,
      selected_date,
      selected_time,
      location_id,
      status,
      late_minutes,
      reschedule_requested_by,
      reschedule_response,
      reschedule_new_date,
      reschedule_new_time,
      locations (
        store_name,
        address,
        city,
        state
      )
    `)
    .in('trade_id', tradeIds)
    .eq('status', 'confirmed')
    .not('selected_date', 'is', null)
    .not('selected_time', 'is', null);

  if (error) {
    console.error('Error fetching meetups:', error);
    return [];
  }

  // Create schedule map
  const scheduleMap = {};
  (schedules || []).forEach(s => {
    scheduleMap[s.trade_id] = s;
  });

  // First group ALL trades by partner
  const allGroupedByPartner = {};
  userTrades.forEach(trade => {
    const isRequester = trade.requester_id === userId;
    const partnerId = isRequester ? trade.card_owner_id : trade.requester_id;
    const partner = isRequester ? trade.card_owner : trade.requester;

    if (!allGroupedByPartner[partnerId]) {
      allGroupedByPartner[partnerId] = {
        trades: [],
        partner,
        schedule: null,
      };
    }
    allGroupedByPartner[partnerId].trades.push(trade);

    // If this trade has a schedule, use it for the group
    const schedule = scheduleMap[trade.id];
    if (schedule && !allGroupedByPartner[partnerId].schedule) {
      allGroupedByPartner[partnerId].schedule = schedule;
    }
  });

  // Filter to only groups that have a confirmed schedule
  const groupedByPartner = {};
  Object.entries(allGroupedByPartner).forEach(([partnerId, group]) => {
    if (group.schedule) {
      groupedByPartner[partnerId] = group;
    }
  });

  // Transform the data
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = today.getFullYear();

  const meetupsWithDates = Object.entries(groupedByPartner)
    .map(([partnerId, group]) => {
      const { trades, partner, schedule } = group;
      const primaryTrade = trades[0];

      const tradePartner = partner?.username || 'Trader';

      // Collect all cards
      const cards = trades.map(t => ({
        name: t.cards?.name || 'Unknown Card',
        image: t.cards?.digital_image_url || '',
      }));

      // Get location name
      const location = schedule.locations;
      const locationName = location?.store_name || `${location?.city}, ${location?.state}` || 'TBD';

      // Parse date correctly (format is "12/1" - month/day)
      let meetupDate = null;
      if (schedule.selected_date) {
        const [month, day] = schedule.selected_date.split('/').map(Number);
        meetupDate = new Date(currentYear, month - 1, day);

        // If date appears to be more than 6 months in the future,
        // it's probably from last year (e.g., "12/10" when we're in January)
        const sixMonthsFromNow = new Date(today);
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

        if (meetupDate > sixMonthsFromNow) {
          meetupDate = new Date(currentYear - 1, month - 1, day);
        } else if (meetupDate < today) {
          // If it's in the past this year, check if it should be next year
          // (e.g., "1/15" when we're in late December)
          const nextYearDate = new Date(currentYear + 1, month - 1, day);
          if (nextYearDate <= sixMonthsFromNow) {
            meetupDate = nextYearDate;
          }
        }
      }

      // Check if the OTHER user needs to act (late notice or reschedule pending)
      const hasLateNotice = schedule.late_minutes && !schedule.reschedule_response;
      const hasReschedule = schedule.reschedule_new_date && !schedule.reschedule_response;
      const iRequestedIt = schedule.reschedule_requested_by === userId;
      // needsAction = there's a pending notice AND I didn't create it (I need to respond)
      const needsAction = (hasLateNotice || hasReschedule) && !iRequestedIt;
      // partnerNeedsAction = there's a pending notice AND I created it (they need to respond)
      const partnerNeedsAction = (hasLateNotice || hasReschedule) && iRequestedIt;
      // hasResponse = there's a response to the late/reschedule notice (either side should see it)
      const hasResponse = !!schedule.reschedule_response;

      return {
        id: primaryTrade.id,
        partnerName: tradePartner,
        time: schedule.selected_time,
        date: schedule.selected_date,
        parsedDate: meetupDate,
        location: locationName,
        cardImage: cards[0].image,
        cards, // All cards for fan display
        cardCount: cards.length,
        needsAction,
        partnerNeedsAction,
        hasResponse,
        rescheduleResponse: schedule.reschedule_response,
        lateMinutes: schedule.late_minutes,
      };
    })
    .filter((meetup) => {
      if (!meetup.parsedDate) return false;
      return meetup.parsedDate >= today;
    })
    .sort((a, b) => a.parsedDate - b.parsedDate);

  return meetupsWithDates;
};

// Fetch request counts (grouped by partner - multiple requests from same user = 1 group)
const fetchRequestCounts = async (userId) => {
  // Fetch all requests to group by partner
  const { data: allRequests, error } = await supabase
    .from('trade_requests')
    .select('id, requester_id, card_owner_id, status')
    .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
    .in('status', ['pending', 'accepted', 'declined']);

  if (error) {
    console.error('Error fetching request counts:', error);
    return { incoming: 0, outgoing: 0, accepted: 0, declined: 0 };
  }

  // Group by partner + status to count unique trading partners
  const countGroups = (requests, filterFn) => {
    const filtered = requests.filter(filterFn);
    const partners = new Set();
    filtered.forEach(r => {
      const isIncoming = r.card_owner_id === userId;
      const partnerId = isIncoming ? r.requester_id : r.card_owner_id;
      partners.add(`${partnerId}-${r.status}`);
    });
    return partners.size;
  };

  return {
    incoming: countGroups(allRequests, r => r.card_owner_id === userId && r.status === 'pending'),
    outgoing: countGroups(allRequests, r => r.requester_id === userId && r.status === 'pending'),
    accepted: countGroups(allRequests, r => r.status === 'accepted'),
    declined: countGroups(allRequests, r => r.status === 'declined'),
  };
};

// Fetch active chat/message counts by urgency (grouped by partner)
const fetchMessageCounts = async (userId) => {
  // Get accepted trades where user is involved and schedule is not confirmed
  const { data: activeChats, error } = await supabase
    .from('trade_requests')
    .select(`
      id,
      requester_id,
      card_owner_id,
      accepted_at,
      last_read_requester_at,
      last_read_owner_at,
      trade_schedules (status)
    `)
    .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
    .eq('status', 'accepted');

  if (error) {
    console.error('Error fetching active chats:', error);
    return { lastDay: 0, lastDayUnread: 0, twoDays: 0, twoDaysUnread: 0, threeDays: 0, threeDaysUnread: 0, totalUnread: 0 };
  }

  // Filter to only unconfirmed (active) chats
  const unconfirmed = (activeChats || []).filter(chat => {
    const schedule = chat.trade_schedules?.[0] || chat.trade_schedules;
    return !schedule || schedule.status !== 'confirmed';
  });

  // Group by partner - multiple cards from same person = 1 conversation
  const groupedByPartner = {};
  unconfirmed.forEach(chat => {
    const isRequester = chat.requester_id === userId;
    const partnerId = isRequester ? chat.card_owner_id : chat.requester_id;
    const lastReadAt = isRequester ? chat.last_read_requester_at : chat.last_read_owner_at;

    if (!groupedByPartner[partnerId]) {
      groupedByPartner[partnerId] = {
        earliestAccepted: new Date(chat.accepted_at || Date.now()),
        tradeIds: [chat.id],
        lastReadAt: lastReadAt ? new Date(lastReadAt) : null,
      };
    } else {
      groupedByPartner[partnerId].tradeIds.push(chat.id);
      // Use earliest accepted_at for urgency
      const thisAccepted = new Date(chat.accepted_at || Date.now());
      if (thisAccepted < groupedByPartner[partnerId].earliestAccepted) {
        groupedByPartner[partnerId].earliestAccepted = thisAccepted;
      }
      // Use latest lastReadAt
      if (lastReadAt) {
        const thisRead = new Date(lastReadAt);
        if (!groupedByPartner[partnerId].lastReadAt || thisRead > groupedByPartner[partnerId].lastReadAt) {
          groupedByPartner[partnerId].lastReadAt = thisRead;
        }
      }
    }
  });

  const now = new Date();
  let lastDay = 0, twoDays = 0, threeDays = 0;
  let lastDayUnread = 0, twoDaysUnread = 0, threeDaysUnread = 0;
  let totalUnread = 0;

  // Count grouped conversations by urgency and fetch unread counts
  for (const [partnerId, group] of Object.entries(groupedByPartner)) {
    const hoursSinceAccepted = (now - group.earliestAccepted) / (1000 * 60 * 60);
    const hoursLeft = Math.max(0, 72 - hoursSinceAccepted);

    // Get unread count for this conversation group
    let unreadQuery = supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .in('trade_request_id', group.tradeIds)
      .eq('is_ignored', false)
      .neq('sender_id', userId);

    if (group.lastReadAt) {
      unreadQuery = unreadQuery.gt('created_at', group.lastReadAt.toISOString());
    }

    const { count } = await unreadQuery;
    const hasUnread = (count || 0) > 0;
    totalUnread += count || 0;

    // Skip expired chats (past 72 hours)
    if (hoursLeft <= 0) {
      // Don't count expired in urgency
      continue;
    } else if (hoursLeft <= 24) {
      lastDay++;
      if (hasUnread) lastDayUnread++;
    } else if (hoursLeft <= 48) {
      twoDays++;
      if (hasUnread) twoDaysUnread++;
    } else {
      threeDays++;
      if (hasUnread) threeDaysUnread++;
    }
  }

  return {
    lastDay,
    lastDayUnread,
    twoDays,
    twoDaysUnread,
    threeDays,
    threeDaysUnread,
    totalUnread,
  };
};

// Fetch unseen request count (grouped by partner)
const fetchUnseenRequestCount = async (userId) => {
  const { data, error } = await supabase
    .from('trade_requests')
    .select('id, requester_id')
    .eq('card_owner_id', userId)
    .eq('status', 'pending')
    .eq('viewed_by_owner', false);

  if (error) {
    console.error('Error fetching unseen request count:', error);
    return 0;
  }

  // Count unique requesters (groups), not individual requests
  const uniqueRequesters = new Set(data?.map(r => r.requester_id) || []);
  return uniqueRequesters.size;
};

// Fetch history stats
const fetchHistoryStats = async (userId) => {
  const [completed, incomplete, messagesStarted, scheduled] = await Promise.all([
    // Completed meetups
    supabase
      .from('meetups')
      .select('id', { count: 'exact', head: true })
      .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`)
      .eq('status', 'completed'),

    // Incomplete meetups
    supabase
      .from('meetups')
      .select('id', { count: 'exact', head: true })
      .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`)
      .eq('status', 'incomplete'),

    // Messages started = accepted trade requests (chat opened)
    supabase
      .from('trade_requests')
      .select('id', { count: 'exact', head: true })
      .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
      .eq('status', 'accepted'),

    // Trades scheduled = confirmed trade schedules (fetch trade_id, then filter by user's trades)
    (async () => {
      // First get all trade IDs where user is involved
      const { data: userTrades } = await supabase
        .from('trade_requests')
        .select('id')
        .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`);

      if (!userTrades || userTrades.length === 0) return { count: 0 };

      const tradeIds = userTrades.map(t => t.id);

      // Then count confirmed schedules for those trades
      const result = await supabase
        .from('trade_schedules')
        .select('id', { count: 'exact', head: true })
        .in('trade_id', tradeIds)
        .eq('status', 'confirmed');

      return result;
    })(),
  ]);

  return {
    completed: completed?.count || 0,
    incomplete: incomplete?.count || 0,
    messagesStarted: messagesStarted?.count || 0,
    scheduled: scheduled?.count || 0,
  };
};

// Fetch history items (trade reviews)
const fetchHistoryItems = async (userId) => {
  const { data, error } = await supabase
    .from('trade_reviews')
    .select(`
      id,
      trade_schedule_id,
      reviewer_id,
      created_at,
      trade_schedules (
        selected_date,
        trade_id,
        trade_requests (
          requester_id,
          card_owner_id,
          cards (name, digital_image_url),
          requester:users!trade_requests_requester_id_fkey (username),
          card_owner:users!trade_requests_card_owner_id_fkey (username)
        )
      ),
      user_rating:user_ratings (rating)
    `)
    .eq('reviewer_id', userId)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) {
    console.error('Error fetching trade reviews:', error);
    return [];
  }

  const formattedHistory = data?.map((review) => {
    const tradeRequest = review.trade_schedules?.trade_requests;
    const isRequester = tradeRequest?.requester_id === userId;
    const partnerName = isRequester
      ? tradeRequest?.card_owner?.username || 'Trader'
      : tradeRequest?.requester?.username || 'Trader';

    const cardName = tradeRequest?.cards?.name || 'Unknown Card';
    const rating = review.user_rating?.rating || 0;
    const tradeDate = review.trade_schedules?.selected_date
      ? new Date(review.trade_schedules.selected_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : 'Unknown date';

    return {
      id: review.id,
      partnerName: partnerName,
      cardName: cardName,
      date: tradeDate,
      rating: Math.round(rating),
      type: rating >= 3 ? 'good' : 'bad',
    };
  }) || [];

  return formattedHistory;
};

// Upcoming Meetup Card Component
const MeetupCard = ({ meetup, onClick }) => {
  const { theme } = useTheme();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [month, day] = dateStr.split('/').map(Number);
    const year = new Date().getFullYear();
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get cards for fan display
  const cards = meetup.cards || [{ image: meetup.cardImage }];
  const hasMultiple = cards.length > 1;

  // Render fanned cards
  const renderCardFan = () => {
    const visibleCards = cards.slice(0, 3);
    return (
      <div style={styles.meetupCardFan}>
        {visibleCards.map((card, index) => (
          <CardImage
            key={index}
            digitalImage={card.image}
            name={card.name}
            theme={theme}
            style={{
              ...styles.meetupCardImage,
              ...styles.meetupFannedCard,
              backgroundColor: theme.border,
              zIndex: 3 - index,
              transform: `translateX(${index * 10}px) rotate(${index * 3}deg)`,
            }}
          />
        ))}
        {cards.length > 3 && (
          <div style={{ ...styles.meetupMoreIndicator, backgroundColor: theme.accent }}>
            <span style={{ ...styles.meetupMoreText, color: theme.bgPure }}>+{cards.length - 3}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <button style={{ ...styles.meetupCard, position: 'relative' }} onClick={onClick}>
      {/* Action needed badge */}
      {meetup.needsAction && (
        <div style={{
          position: 'absolute', top: 4, right: 4, zIndex: 5,
          backgroundColor: '#ef4444', borderRadius: 10,
          padding: '2px 8px', minWidth: 20,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>Action Needed</span>
        </div>
      )}
      {/* Waiting on them indicator */}
      {meetup.partnerNeedsAction && (
        <div style={{
          position: 'absolute', top: 4, right: 4, zIndex: 5,
          backgroundColor: theme.warning || '#f59e0b', borderRadius: 10,
          padding: '2px 8px', minWidth: 20,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>Waiting</span>
        </div>
      )}
      {/* Partner responded to my late/reschedule notice */}
      {meetup.hasResponse && (
        <div style={{
          position: 'absolute', top: 4, right: 4, zIndex: 5,
          backgroundColor: meetup.rescheduleResponse === 'accepted' ? '#22c55e' : '#ef4444',
          borderRadius: 10, padding: '2px 8px', minWidth: 20,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>
            {meetup.rescheduleResponse === 'accepted' ? 'Still On' : 'Cancelled'}
          </span>
        </div>
      )}
      {hasMultiple ? (
        renderCardFan()
      ) : (
        <div style={styles.meetupSingleCardWrapper}>
          <CardImage
            digitalImage={meetup.cardImage}
            name={meetup.cardName}
            theme={theme}
            style={{ ...styles.meetupCardImage, backgroundColor: theme.border }}
          />
        </div>
      )}
      <span style={{ ...styles.meetupPartner, color: theme.text }}>{meetup.partnerName}</span>
      <span style={{ ...styles.meetupTime, color: theme.success }}>{meetup.time}</span>
      <span style={{ ...styles.meetupDate, color: theme.success }}>{formatDate(meetup.date)}</span>
      <span style={{ ...styles.meetupLocation, color: theme.textSecondary }}>{meetup.location}</span>
    </button>
  );
};

const ViewAllCard = ({ onClick }) => {
  const { theme } = useTheme();
  return (
    <button style={{ ...styles.viewAllCard, background: theme.bgCard, borderColor: theme.accent, color: theme.accent }} onClick={onClick}>
      <CalendarIcon />
      <span style={{ ...styles.viewAllText, color: theme.accent }}>View All</span>
    </button>
  );
};

const EmptyMeetups = () => {
  const { theme } = useTheme();
  return (
    <div style={{ ...styles.emptyMeetups, backgroundColor: theme.bgCard, color: theme.textSecondary }}>
      <CalendarIcon />
      <span style={{ ...styles.emptyTitle, color: theme.text }}>No upcoming meetups</span>
      <span style={{ ...styles.emptySubtitle, color: theme.textSecondary }}>Schedule a meetup to see previews here</span>
    </div>
  );
};

// Messages Tab Content
const MessagesTab = ({ data, onNavigate }) => {
  const { theme } = useTheme();
  const totalChats = data.lastDay + data.twoDays + data.threeDays;

  const UrgencyRow = ({ level, count, unread, title, subtitle, onClick }) => {
    const levelColors = {
      critical: theme.error,
      warning: theme.warning,
      normal: theme.accent,
    };
    const color = levelColors[level];

    return (
      <button style={{ ...styles.urgencyRow, backgroundColor: theme.bgCard, borderColor: theme.border }} onClick={onClick}>
        <div style={{ ...styles.urgencyIndicator, backgroundColor: color }} />
        <div style={styles.urgencyContent}>
          <span style={{ ...styles.urgencyTitle, color: color }}>{title}</span>
          <span style={{ ...styles.urgencyCount, color: theme.text }}>{count} in progress</span>
          <span style={{ ...styles.urgencySubtitle, color: theme.textMuted }}>{subtitle}</span>
        </div>
        {unread > 0 && (
          <div style={{ ...styles.unreadBadge, backgroundColor: color }}>
            <span style={{ ...styles.unreadText, color: theme.text }}>{unread > 99 ? '99+' : unread}</span>
          </div>
        )}
        <ChevronRightIcon />
      </button>
    );
  };

  return (
    <div style={styles.tabContent}>
      <div style={{ ...styles.thermometer, backgroundColor: theme.bgCard, borderColor: theme.border }}>
        <div style={styles.thermometerHeader}>
          <span style={{ ...styles.thermometerTitle, color: theme.text }}>Time Sensitive</span>
          <span style={{ ...styles.thermometerTotal, color: theme.textMuted }}>{totalChats} total</span>
        </div>
        <div style={styles.urgencyList}>
          <UrgencyRow
            level="critical"
            count={data.lastDay}
            unread={data.lastDayUnread}
            title="Last Day"
            subtitle="Expiring today"
            onClick={() => onNavigate('/messages?filter=last_day')}
          />
          <UrgencyRow
            level="warning"
            count={data.twoDays}
            unread={data.twoDaysUnread}
            title="2 Days Left"
            subtitle="Get these squared away"
            onClick={() => onNavigate('/messages?filter=2_days')}
          />
          <UrgencyRow
            level="normal"
            count={data.threeDays}
            unread={data.threeDaysUnread}
            title="3 Days Left"
            subtitle="Start planning"
            onClick={() => onNavigate('/messages?filter=3_days')}
          />
        </div>
      </div>
    </div>
  );
};

// Requests Tab Content
const RequestsTab = ({ data, unseenCount, onNavigate }) => {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      onNavigate(`/requests?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSearchClick = () => {
    if (searchQuery.trim()) {
      onNavigate(`/requests?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div style={styles.tabContent}>
      {/* Search Bar */}
      <div style={{ ...styles.searchInputWrapper, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search by name or card..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearch}
          style={{ ...styles.searchInput, color: theme.text }}
        />
        {searchQuery && (
          <button style={{ ...styles.searchGoButton, backgroundColor: theme.accent, color: theme.bgPure }} onClick={handleSearchClick}>
            Go
          </button>
        )}
      </div>

      {/* Top Grid - Incoming/Outgoing */}
      <div style={styles.requestGrid}>
        <button
          style={{ ...styles.requestCard, backgroundColor: theme.bgCard, borderColor: theme.border }}
          onClick={() => onNavigate('/requests?filter=incoming')}
        >
          <div style={{ ...styles.requestCardIcon, color: theme.accent }}>
            <ArrowDownCircleIcon />
          </div>
          <span style={{ ...styles.requestCardTitle, color: theme.text }}>Incoming</span>
          <span style={{ ...styles.requestCardSubtitle, color: theme.textMuted }}>Trade requests</span>
          {/* Count */}
          {data.incoming > 0 && (
            <div style={styles.requestBadge}>
              <span style={{ ...styles.requestBadgeText, color: theme.textMuted }}>({data.incoming > 99 ? '99+' : data.incoming})</span>
            </div>
          )}
          {/* Red NEW badge */}
          {unseenCount > 0 && (
            <div style={{ ...styles.newBadge, backgroundColor: theme.error }}>
              <span style={{ ...styles.newBadgeText, color: theme.text }}>NEW</span>
            </div>
          )}
        </button>
        <button
          style={{ ...styles.requestCard, backgroundColor: theme.bgCard, borderColor: theme.border }}
          onClick={() => onNavigate('/requests?filter=outgoing')}
        >
          <div style={{ ...styles.requestCardIcon, color: theme.accent }}>
            <ArrowUpCircleIcon />
          </div>
          <span style={{ ...styles.requestCardTitle, color: theme.text }}>Outgoing</span>
          <span style={{ ...styles.requestCardSubtitle, color: theme.textMuted }}>Sent requests</span>
          {data.outgoing > 0 && (
            <div style={styles.requestBadge}>
              <span style={{ ...styles.requestBadgeText, color: theme.textMuted }}>({data.outgoing > 99 ? '99+' : data.outgoing})</span>
            </div>
          )}
        </button>
      </div>

      {/* Full Width Cards */}
      <button
        style={{ ...styles.fullWidthCard, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.accent }}
        onClick={() => onNavigate('/requests?filter=accepted')}
      >
        <CheckCircleIcon />
        <div style={styles.fullCardInfo}>
          <span style={{ ...styles.fullCardTitle, color: theme.text }}>Accepted Requests</span>
          <span style={{ ...styles.fullCardSubtitle, color: theme.textMuted }}>Ready to schedule meetup</span>
        </div>
        {data.accepted > 0 && (
          <div style={styles.fullWidthBadge}>
            <span style={{ ...styles.requestBadgeText, color: theme.textMuted }}>({data.accepted > 99 ? '99+' : data.accepted})</span>
          </div>
        )}
        <ChevronRightIcon />
      </button>

      <button
        style={{ ...styles.fullWidthCard, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.accent }}
        onClick={() => onNavigate('/requests?filter=declined')}
      >
        <XCircleIcon />
        <div style={styles.fullCardInfo}>
          <span style={{ ...styles.fullCardTitle, color: theme.text }}>Declined Requests</span>
          <span style={{ ...styles.fullCardSubtitle, color: theme.textMuted }}>Try again or move on</span>
        </div>
        {data.declined > 0 && (
          <div style={styles.fullWidthBadge}>
            <span style={{ ...styles.requestBadgeText, color: theme.textMuted }}>({data.declined > 99 ? '99+' : data.declined})</span>
          </div>
        )}
        <ChevronRightIcon />
      </button>
    </div>
  );
};

// History Tab Content
const HistoryTab = ({ stats, items, onNavigate }) => {
  const { theme } = useTheme();
  const [showRatesModal, setShowRatesModal] = useState(false);

  // Calculate all rates
  const successRate = stats.completed + stats.incomplete > 0
    ? Math.round((stats.completed / (stats.completed + stats.incomplete)) * 100)
    : 0;
  const messageToScheduledRate = stats.messagesStarted > 0
    ? Math.round((stats.scheduled / stats.messagesStarted) * 100)
    : 0;
  const scheduledToCompletedRate = stats.scheduled > 0
    ? Math.round((stats.completed / stats.scheduled) * 100)
    : 0;
  const messageToCompletedRate = stats.messagesStarted > 0
    ? Math.round((stats.completed / stats.messagesStarted) * 100)
    : 0;

  return (
    <div style={styles.tabContent}>
      {/* Conversion Rates Modal */}
      {showRatesModal && (
        <div style={styles.ratesModalOverlay} onClick={() => setShowRatesModal(false)}>
          <div style={{ ...styles.ratesModal, backgroundColor: theme.bgCard, borderColor: theme.border }} onClick={e => e.stopPropagation()}>
            <div style={styles.ratesModalHeader}>
              <span style={{ ...styles.ratesModalTitle, color: theme.text }}>Conversion Rates</span>
              <button style={{ ...styles.ratesModalClose, color: theme.textMuted }} onClick={() => setShowRatesModal(false)}>X</button>
            </div>
            <div style={styles.ratesModalContent}>
              <div style={styles.rateRow}>
                <span style={{ ...styles.rateLabel, color: theme.textSecondary }}>Message to Scheduled</span>
                <span style={{ ...styles.rateValue, color: theme.accent }}>{messageToScheduledRate}%</span>
              </div>
              <div style={styles.rateRow}>
                <span style={{ ...styles.rateLabel, color: theme.textSecondary }}>Scheduled to Completed</span>
                <span style={{ ...styles.rateValue, color: theme.success }}>{scheduledToCompletedRate}%</span>
              </div>
              <div style={styles.rateRow}>
                <span style={{ ...styles.rateLabel, color: theme.textSecondary }}>Message to Completed</span>
                <span style={{ ...styles.rateValue, color: theme.warning }}>{messageToCompletedRate}%</span>
              </div>
              <div style={{ ...styles.rateDivider, backgroundColor: theme.border }} />
              <div style={styles.rateRow}>
                <span style={{ ...styles.rateLabel, color: theme.textSecondary }}>Overall Success Rate</span>
                <span style={{ ...styles.rateValue, color: theme.success }}>{successRate}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar - Scrollable */}
      <div style={{ ...styles.statsBarScroll, backgroundColor: theme.bgCard, borderColor: theme.border }}>
        <div style={styles.statsBarInner}>
          <div style={styles.statItem} onClick={() => onNavigate('/messages')}>
            <span style={{ ...styles.statNumber, color: theme.accent }}>{stats.messagesStarted}</span>
            <span style={{ ...styles.statLabel, color: theme.textMuted }}>Started</span>
          </div>
          <div style={{ ...styles.statDivider, backgroundColor: theme.border }} />
          <div style={styles.statItem} onClick={() => onNavigate('/trades?filter=scheduled')}>
            <span style={{ ...styles.statNumber, color: theme.warning }}>{stats.scheduled}</span>
            <span style={{ ...styles.statLabel, color: theme.textMuted }}>Scheduled</span>
          </div>
          <div style={{ ...styles.statDivider, backgroundColor: theme.border }} />
          <div style={styles.statItem} onClick={() => onNavigate('/trades?filter=completed')}>
            <span style={{ ...styles.statNumber, color: theme.success }}>{stats.completed}</span>
            <span style={{ ...styles.statLabel, color: theme.textMuted }}>Completed</span>
          </div>
          <div style={{ ...styles.statDivider, backgroundColor: theme.border }} />
          <div style={styles.statItem} onClick={() => onNavigate('/trades?filter=incomplete')}>
            <span style={{ ...styles.statNumber, color: theme.textSecondary }}>{stats.incomplete}</span>
            <span style={{ ...styles.statLabel, color: theme.textMuted }}>Incomplete</span>
          </div>
          <div style={{ ...styles.statDivider, backgroundColor: theme.border }} />
          <div style={{ ...styles.statItem, cursor: 'pointer' }} onClick={() => setShowRatesModal(true)}>
            <span style={{ ...styles.statNumber, color: theme.accent }}>{successRate}%</span>
            <span style={{ ...styles.statLabel, color: theme.textMuted }}>Rate</span>
          </div>
        </div>
      </div>

      {/* History List */}
      <div style={styles.historyList}>
        {items.length > 0 ? (
          items.map((item) => (
            <button
              key={item.id}
              style={{
                ...styles.historyItem,
                backgroundColor: theme.bgCard,
                borderColor: theme.border,
                borderLeftColor: item.type === 'good' ? theme.success : theme.error,
              }}
              onClick={() => onNavigate(`/trade/${item.id}`)}
            >
              <div style={styles.historyContent}>
                <span style={{ ...styles.historyTitle, color: theme.text }}>Trade with {item.partnerName}</span>
                <span style={{ ...styles.historyDetail, color: theme.textMuted }}>{item.cardName} - {item.date}</span>
              </div>
              <div style={styles.historyRating}>
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} filled={i < item.rating} />
                ))}
              </div>
            </button>
          ))
        ) : (
          <div style={{ ...styles.emptyHistory, backgroundColor: theme.bgCard, color: theme.textSecondary }}>
            <CalendarIcon />
            <span style={{ ...styles.emptyHistoryTitle, color: theme.text }}>No trade history yet</span>
            <span style={{ ...styles.emptyHistorySubtitle, color: theme.textSecondary }}>Complete trades to see your history here</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Main MeetUp Component
const MeetUp = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { setChatBadgeCount } = useUI();
  const { currentStep, handleTradeTutorialComplete } = useTutorial();
  const { theme, isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('messages');
  const [loading, setLoading] = useState(true);

  // State for data
  const [meetups, setMeetups] = useState([]);
  // Message counts with unread tracking
  const [messageData, setMessageData] = useState({
    lastDay: 0,
    lastDayUnread: 0,
    twoDays: 0,
    twoDaysUnread: 0,
    threeDays: 0,
    threeDaysUnread: 0,
    totalUnread: 0,
  });
  const [requestData, setRequestData] = useState({ incoming: 0, outgoing: 0, accepted: 0, declined: 0 });
  const [historyData, setHistoryData] = useState({ completed: 0, incomplete: 0, messagesStarted: 0, scheduled: 0 });
  const [historyItems, setHistoryItems] = useState([]);
  const [unseenRequestCount, setUnseenRequestCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Use activeProfile.id directly (handles parent viewing as child)
      const userId = activeProfile?.id;
      if (!userId) {
        console.log('No user ID found, skipping queries');
        setLoading(false);
        return;
      }

      try {
        // Fetch all data in parallel
        const [
          meetupsData,
          requestCounts,
          unseenCount,
          historyStats,
          historyItemsData,
          messageCounts,
        ] = await Promise.all([
          fetchMeetups(userId),
          fetchRequestCounts(userId),
          fetchUnseenRequestCount(userId),
          fetchHistoryStats(userId),
          fetchHistoryItems(userId),
          fetchMessageCounts(userId),
        ]);

        setMeetups(meetupsData);
        setRequestData(requestCounts);
        setUnseenRequestCount(unseenCount);
        setHistoryData(historyStats);
        setHistoryItems(historyItemsData);
        setMessageData(messageCounts);

        // Update bottom nav badge: unread messages + unseen requests
        const totalBadge = (messageCounts.totalUnread || 0) + unseenCount;
        setChatBadgeCount(totalBadge);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeProfile?.id, setChatBadgeCount]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleProfilePress = () => {
    // Navigate to profile/settings
    navigate('/profile');
  };

  const totalUnread = messageData.lastDayUnread + messageData.twoDaysUnread + messageData.threeDaysUnread;

  const tabs = [
    { id: 'messages', label: 'In Progress', badge: totalUnread },
    { id: 'requests', label: 'Requests', badge: unseenRequestCount },
    { id: 'history', label: 'History', badge: 0 },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'messages':
        return <MessagesTab data={messageData} onNavigate={handleNavigate} />;
      case 'requests':
        return <RequestsTab data={requestData} unseenCount={unseenRequestCount} onNavigate={handleNavigate} />;
      case 'history':
        return <HistoryTab stats={historyData} items={historyItems} onNavigate={handleNavigate} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <div style={styles.loadingContainer}>
          <div style={{ ...styles.spinner, borderColor: theme.border, borderTopColor: theme.accent }} />
        </div>
      </div>
    );
  }

  // Guest mode: show sign-in prompt instead of trade hub
  if (!user && isGuest()) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg }}>
        <GuestEmptyState
          title="Sign in to start trading"
          body="Sign in to send trade requests, schedule meetups at verified shops, and chat with other collectors. Every message is button-driven for safety."
          action="Create Account"
        />
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: theme.bg }}>
      {/* Trade Tutorial - Chat Intro */}
      <TutorialBackdrop visible={currentStep === TUTORIAL_STEPS.TRADE_CHAT_INTRO} />
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.TRADE_CHAT_INTRO}
        mode="normal"
        characterImage={ChaseWaveImage}
        message={[
          "This is your trade hub!",
          "",
          "Incoming and outgoing requests,",
          "messages, and scheduled meetups",
          "all live here. It's that simple!"
        ]}
        hideButtons={false}
        yesText="Done"
        noText=""
        onYes={handleTradeTutorialComplete}
      />

      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: theme.bg }}>
        <h1 style={{ ...styles.headerTitle, color: theme.text }}>Meet Up</h1>
        <div style={styles.headerButtons}>
          <button style={{ ...styles.iconButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }} onClick={handleProfilePress}>
            <UserIcon />
          </button>
        </div>
        <div style={{ ...styles.headerBorder, backgroundColor: `rgba(${isDark ? '255, 255, 255' : '0, 0, 0'}, 0.35)` }} />
      </div>

      {/* Scrollable Content */}
      <div style={styles.scrollContent}>
        {/* Upcoming Meetups Carousel */}
        <div style={styles.upcomingSection}>
          {meetups.length > 0 ? (
            <div style={styles.meetupCarousel}>
              {meetups.slice(0, 4).map((meetup) => (
                <MeetupCard
                  key={meetup.id}
                  meetup={meetup}
                  onClick={() => handleNavigate(
                    (meetup.needsAction || meetup.hasResponse) ? `/chat/${meetup.id}` : `/trades?highlight=${meetup.id}`
                  )}
                />
              ))}
              {meetups.length > 3 && (
                <ViewAllCard onClick={() => handleNavigate('/trades?filter=scheduled')} />
              )}
            </div>
          ) : (
            <EmptyMeetups />
          )}
        </div>

        {/* Tab Bar */}
        <div style={{ ...styles.tabBar, backgroundColor: theme.bgSheet }}>
          {/* Sliding Indicator */}
          <div
            style={{
              ...styles.tabIndicator,
              backgroundColor: theme.accent,
              transform: `translateX(${activeTab === 'messages' ? '0' : activeTab === 'requests' ? '100%' : '200%'})`,
            }}
          />
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                style={styles.tab}
                onClick={() => setActiveTab(tab.id)}
              >
                <span style={{
                  ...styles.tabLabel,
                  color: isActive ? '#fff' : theme.textSecondary,
                }}>
                  {tab.label}
                </span>
                {tab.badge > 0 && (
                  <div style={{ ...styles.tabBadge, backgroundColor: theme.error }}>
                    <span style={{ ...styles.tabBadgeText, color: '#fff' }}>{tab.badge > 99 ? '99+' : tab.badge}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    flexShrink: 0,
    position: 'relative',
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: '40px',
    right: '40px',
    height: '2px',
    borderRadius: '1px',
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  iconButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },

  // Scroll Content
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px 100px 20px',
  },

  // Upcoming Meetups
  upcomingSection: {
    marginBottom: 16,
  },
  meetupCarousel: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 8,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  meetupCard: {
    flexShrink: 0,
    width: 80,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  meetupCardImage: {
    width: 64,
    height: 90,
    borderRadius: 6,
    objectFit: 'contain',
  },
  meetupSingleCardWrapper: {
    width: 80,
    height: 95,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  // Card fan styles for meetup cards
  meetupCardFan: {
    position: 'relative',
    width: 80,
    height: 95,
    marginLeft: 8,
  },
  meetupFannedCard: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 60,
    height: 84,
    transformOrigin: 'bottom left',
    boxShadow: '2px 2px 8px rgba(0,0,0,0.3)',
  },
  meetupMoreIndicator: {
    position: 'absolute',
    bottom: 4,
    right: 0,
    borderRadius: 10,
    padding: '2px 6px',
    zIndex: 10,
  },
  meetupMoreText: {
    fontSize: 9,
    fontWeight: 700,
    marginBottom: 6,
  },
  meetupPartner: {
    fontSize: 10,
    fontWeight: 600,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 2,
  },
  meetupTime: {
    fontSize: 9,
    fontWeight: 600,
    textAlign: 'center',
  },
  meetupDate: {
    fontSize: 9,
    textAlign: 'center',
    marginBottom: 2,
  },
  meetupLocation: {
    fontSize: 8,
    textAlign: 'center',
    maxWidth: 80,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  viewAllCard: {
    flexShrink: 0,
    width: 80,
    height: 112,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    border: '2px dashed',
    borderRadius: 6,
    cursor: 'pointer',
  },
  viewAllText: {
    fontSize: 11,
    fontWeight: 700,
  },
  emptyMeetups: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    borderRadius: 12,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  emptySubtitle: {
    fontSize: 12,
  },

  // Tab Bar
  tabBar: {
    position: 'relative',
    display: 'flex',
    borderRadius: 12,
    padding: 4,
    marginBottom: 16,
  },
  tabIndicator: {
    position: 'absolute',
    top: 4,
    left: 4,
    width: 'calc(33.333% - 2.67px)',
    height: 'calc(100% - 8px)',
    borderRadius: 10,
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '10px 12px',
    background: 'none',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    position: 'relative',
    zIndex: 1,
  },
  tabLabel: {
    fontSize: 13,
    fontWeight: 600,
  },
  tabBadge: {
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  tabBadgeText: {
    fontSize: 9,
    fontWeight: 700,
  },

  // Tab Content
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  // Search
  searchInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 12,
    border: '1px solid',
  },
  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    fontSize: 14,
  },
  searchGoButton: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Messages Tab - Thermometer
  thermometer: {
    borderRadius: 16,
    padding: 20,
    border: '1px solid',
  },
  thermometerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  thermometerTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  thermometerTotal: {
    fontSize: 12,
  },
  urgencyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  urgencyRow: {
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    border: '1px solid',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  urgencyIndicator: {
    width: 8,
    height: 40,
    borderRadius: 4,
    marginRight: 16,
    flexShrink: 0,
  },
  urgencyContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  urgencyTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
  },
  urgencyCount: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
  },
  urgencySubtitle: {
    fontSize: 11,
  },
  unreadBadge: {
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
    marginRight: 8,
  },
  unreadText: {
    fontSize: 12,
    fontWeight: 700,
  },

  // Requests Tab
  requestGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  requestCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
    cursor: 'pointer',
    minHeight: 100,
    textAlign: 'left',
  },
  requestCardIcon: {
    marginBottom: 8,
  },
  requestCardTitle: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 2,
  },
  requestCardSubtitle: {
    fontSize: 11,
  },
  requestBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'transparent',
  },
  requestBadgeText: {
    fontSize: 13,
    fontWeight: 600,
  },
  newBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    borderRadius: 4,
    padding: '2px 6px',
  },
  newBadgeText: {
    fontSize: 9,
    fontWeight: 700,
  },
  fullWidthBadge: {
    backgroundColor: 'transparent',
    marginRight: 4,
  },
  fullWidthCard: {
    display: 'flex',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    border: '1px solid',
    cursor: 'pointer',
    gap: 12,
    width: '100%',
    textAlign: 'left',
  },
  fullCardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  fullCardTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
  },
  fullCardSubtitle: {
    fontSize: 11,
  },

  // History Tab
  statsBarScroll: {
    borderRadius: 12,
    border: '1px solid',
    padding: '16px 0',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  statsBarInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 'max-content',
    paddingLeft: 16,
    paddingRight: 16,
    gap: 8,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 52,
    cursor: 'pointer',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 10,
  },
  statDivider: {
    width: 1,
    height: 32,
    flexShrink: 0,
  },
  // Rates Modal
  ratesModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  ratesModal: {
    width: '85%',
    maxWidth: 320,
    borderRadius: 16,
    border: '1px solid',
    padding: 20,
  },
  ratesModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  ratesModalTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  ratesModalClose: {
    background: 'none',
    border: 'none',
    fontSize: 14,
    cursor: 'pointer',
    padding: 4,
  },
  ratesModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  rateRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rateLabel: {
    fontSize: 13,
  },
  rateValue: {
    fontSize: 18,
    fontWeight: 700,
  },
  rateDivider: {
    height: 1,
    marginTop: 4,
    marginBottom: 4,
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  historyItem: {
    display: 'flex',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    border: '1px solid',
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  historyContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  historyTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
  },
  historyDetail: {
    fontSize: 11,
  },
  historyRating: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  emptyHistory: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 12,
    gap: 8,
  },
  emptyHistoryTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginTop: 8,
  },
  emptyHistorySubtitle: {
    fontSize: 13,
    textAlign: 'center',
  },
};

export default MeetUp;
